import {Component,ElementRef,Renderer2,ViewChild} from '@angular/core';
import {ListViewItem} from "@domain/common/list-view/list-view-item";
import {AvanceImputable} from "@domain/avance/avance-imputable";
import {NatureAvance} from "@domain/avance/nature-avance";
import {SettingsAVState} from "@domain/settings/settings";
import {Subject} from "rxjs";

/**
 * Item de la liste des avances imputables sur une NDF
 *
 * @author Laurent Convert
 * @date 23/04/2026
 */
@Component({
	host: {'data-test-id': 'avances-imputables-list-item'},
	selector: 'avances-imputables-list-item',
	templateUrl: './avances-imputables-list-item.component.html',
	styleUrls: ['./avances-imputables-list-item.component.scss']
})
export class AvancesImputablesListItemComponent extends ListViewItem<AvanceImputable> {
	/* Déclaration pour accès dans le template */
	protected readonly NatureAvance = NatureAvance;

	/** Options supplémentaires passées par la liste */
	extraOptions: {
		deviseEntreprise: string,
		avSettings: SettingsAVState,
		onChangeMontant: Subject<{idAvance: number, montant: number}>
	}

	/** Référence vers l'input d'édition du montant */
	@ViewChild('montantEditInput') montantEditInput!: ElementRef;

	/** Référence vers le span invisible pour calculer la largeur */
	@ViewChild('montantEditInputWidthCalculator') montantEditInputWidthCalculator!: ElementRef;

	/** Flag d'édition active */
	isEditActive: boolean = false;

	/** Montant à imputer (valeur locale pour l'édition) */
	montantImpute: number;

	/** Sauvegarde de la valeur avant édition */
	private backupMontant: number;

	/**
	 * Constructeur
	 *
	 * @param renderer Service de rendu Angular
	 */
	constructor(private renderer: Renderer2) {
		super();
	}

	/**
	 * Initialise le montant à imputer au montant disponible
	 */
	ngOnInit(): void {
		this.montantImpute = this.data.montant;
	}

	/**
	 * Active le mode édition du montant à imputer
	 */
	modifierMontant(): void {
		//Sauvegarde de la valeur actuelle
		this.backupMontant = this.montantImpute;

		//Activation du mode édition
		this.isEditActive = true;

		//Focus sur l'input après affichage
		setTimeout(() => {
			if (this.montantEditInput) {
				this.adjustWidth();
				this.montantEditInput.nativeElement.focus();
				this.montantEditInput.nativeElement.select();
			}
		});
	}

	/**
	 * Filtre les touches non numériques (autorise chiffres, virgule, point, tiret, et touches de contrôle)
	 */
	onKeyDown(event: KeyboardEvent): void {
		//Autoriser les touches de contrôle (Backspace, Delete, Tab, flèches, Home, End, Ctrl+A/C/V/X)
		if (event.ctrlKey || event.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
			return;
		}

		//Autoriser uniquement les chiffres, virgule, point et tiret
		if (!/^[\d.,\-]$/.test(event.key)) {
			event.preventDefault();
		}
	}

	/**
	 * Nettoie la valeur saisie en supprimant les caractères non numériques (en cas de collage)
	 */
	onInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const cleaned = input.value.replace(/[^\d.,\-]/g, '');
		if (input.value !== cleaned) {
			input.value = cleaned;
			this.montantImpute = cleaned as any;
		}
	}

	/**
	 * Ajuste dynamiquement la largeur de l'input d'édition
	 */
	adjustWidth(): void {
		setTimeout(() => {
			if (this.montantEditInputWidthCalculator && this.montantEditInput) {
				this.renderer.setStyle(
					this.montantEditInput.nativeElement,
					'width',
					(this.montantEditInputWidthCalculator.nativeElement.offsetWidth + 10) + 'px'
				);
			}
		});
	}

	/**
	 * Valide la modification du montant (touche Entrée)
	 */
	onValidateMontant(): void {
		//Conversion en nombre (l'input retourne une string)
		const parsed = parseFloat(String(this.montantImpute).replace(',', '.'));

		//Si la valeur saisie n'est pas un nombre valide, on restaure la valeur d'origine
		if (isNaN(parsed)) {
			this.montantImpute = this.backupMontant;
		} else {
			this.montantImpute = parsed;
		}

		//Désactivation du mode édition
		this.isEditActive = false;

		//Notification du changement au composant parent
		this.notifyMontantChange();
	}

	/**
	 * Annule la modification du montant (touche Escape ou perte de focus)
	 */
	onCancelEdit(): void {
		//Restauration de la valeur sauvegardée
		this.montantImpute = this.backupMontant;

		//Désactivation du mode édition
		this.isEditActive = false;
	}


	/**
	 * Vérifie si le montant imputé dépasse le montant disponible
	 */
	isMontantImputeInvalid(): boolean {
		const montant = parseFloat(String(this.montantImpute).replace(',', '.'));
		return !isNaN(montant) && montant > this.data.montant;
	}

	/**
	 * Notifie le composant parent du changement de montant
	 */
	private notifyMontantChange(): void {
		if (this.extraOptions?.onChangeMontant) {
			this.extraOptions.onChangeMontant.next({
				idAvance: this.data.idAvance,
				montant: this.montantImpute
			});
		}
	}

}